import React from "react";
import { Button } from "@/components/ui/button";
import { ShieldCheck, Sparkles, ArrowRight } from "lucide-react";

const HERO_IMAGE =
  "https://images.unsplash.com/photo-1557452765-5b57d100c1dc?auto=format&fit=crop&w=1400&q=80";

const Hero = () => {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative pt-28 md:pt-32 pb-20 md:pb-28 overflow-hidden"
    >
      {/* Decorative background */}
      <div
        aria-hidden="true"
        className="absolute -top-40 -right-40 h-[520px] w-[520px] rounded-full bg-secondary/60 blur-3xl opacity-70"
      />
      <div
        aria-hidden="true"
        className="absolute top-40 -left-32 h-[380px] w-[380px] rounded-full bg-accent/20 blur-3xl"
      />

      <div className="relative max-w-7xl mx-auto px-6 md:px-10 lg:px-16 grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
        {/* Left: copy */}
        <div className="lg:col-span-6 fade-up">
          <div
            data-testid="hero-eyebrow"
            className="inline-flex items-center gap-2 rounded-full border border-border bg-background/60 backdrop-blur px-4 py-1.5 text-xs tracking-widest uppercase text-muted-foreground"
          >
            <Sparkles size={14} className="text-primary" />
            Premium Mobile Childcare
          </div>

          <h1
            data-testid="hero-headline"
            className="mt-6 font-serif-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-foreground"
          >
            Trusted care,{" "}
            <span className="italic text-primary">wherever</span> your family
            goes.
          </h1>

          <p
            data-testid="hero-subheadline"
            className="mt-6 max-w-xl text-base md:text-lg text-muted-foreground leading-relaxed"
          >
            Care Anywhere brings certified, meticulously-vetted caregivers
            directly to your event, hotel suite, or corporate venue — so you
            can be present, knowing your children are in exceptional hands.
          </p>

          <div className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <Button
              asChild
              data-testid="hero-cta-book"
              className="group rounded-full h-12 px-7 bg-primary hover:bg-primary/90 text-primary-foreground text-sm tracking-wide"
            >
              <a href="#inquire" className="flex items-center gap-2">
                Book Care Now
                <ArrowRight
                  size={16}
                  className="transition-transform group-hover:translate-x-0.5"
                />
              </a>
            </Button>
            <Button
              asChild
              variant="outline"
              data-testid="hero-cta-inquire"
              className="rounded-full h-12 px-7 border-primary/30 text-primary hover:bg-primary/5 hover:text-primary text-sm tracking-wide"
            >
              <a href="#services">Inquire About An Event</a>
            </Button>
          </div>

          <div className="mt-10 flex items-center gap-6 text-xs text-muted-foreground">
            <div className="flex items-center gap-2">
              <ShieldCheck size={16} className="text-primary" />
              CPR &amp; First Aid Certified
            </div>
            <div className="hidden sm:block h-4 w-px bg-border" />
            <div className="hidden sm:block">Background Verified</div>
            <div className="hidden md:block h-4 w-px bg-border" />
            <div className="hidden md:block">Fully Insured</div>
          </div>
        </div>

        {/* Right: image */}
        <div className="lg:col-span-6 fade-up" style={{ animationDelay: "120ms" }}>
          <div className="relative">
            <div className="absolute -inset-6 rounded-[2rem] bg-gradient-to-br from-secondary/70 via-accent/20 to-transparent blur-2xl" />
            <div className="relative overflow-hidden rounded-[1.75rem] border border-border shadow-[0_30px_80px_-30px_rgba(63,109,138,0.38)]">
              <img
                src={HERO_IMAGE}
                alt="A caring professional playing with a happy toddler"
                data-testid="hero-image"
                className="w-full h-[480px] md:h-[560px] object-cover"
                loading="eager"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-foreground/25 via-transparent to-transparent" />
            </div>

            {/* Floating trust card */}
            <div
              data-testid="hero-floating-card"
              className="hidden md:flex absolute -bottom-8 -left-8 items-center gap-4 rounded-2xl bg-background/95 backdrop-blur-xl border border-border px-5 py-4 shadow-xl"
            >
              <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
                <ShieldCheck className="text-primary" size={22} />
              </div>
              <div>
                <div className="text-sm font-medium text-foreground">
                  100% Vetted Caregivers
                </div>
                <div className="text-xs text-muted-foreground">
                  TrustLine &middot; Background-Checked
                </div>
              </div>
            </div>

            {/* Floating rating card */}
            <div
              data-testid="hero-rating-card"
              className="hidden md:flex absolute -top-6 -right-6 flex-col items-start gap-1 rounded-2xl bg-primary text-primary-foreground px-5 py-4 shadow-xl"
            >
              <div className="text-xs uppercase tracking-widest opacity-80">
                Trusted by families in
              </div>
              <div className="text-lg font-serif-display">
                40+ cities worldwide
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
